import { useState, useRef, useEffect, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { Dialog, DialogContent } from '@/components/shadcn/dialog';
import TransactionReceiptModal from './TransactionReceiptModal';
import ReceivePaymentModal from './ReceivePaymentModal';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import showToast from '@/components/ui/Toast';
import DropdownButton, { DropdownItem } from '@/components/ui/DropdownButton';
import { invoiceService, ArenaInvoiceApiFilters } from '@/services/invoiceService';
import { Invoice } from '@/types/invoice';
import { InvoiceDetail } from '@/types/invoiceDetail';
import ProductApiService, { Product } from '@/services/productApiService';
import { productOrderService, ProductOrderPayload } from '@/services/productOrderService';
import { PaymentMethodApiService } from '@/services/paymentMethodApiService';
import { requireArenaSlug } from '@/utils/arena';
import { isAppOrQrisTransaction } from '@/utils/dashboardRevenue';
import { cn } from '@/lib/utils';

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];

function formatTransactionDate(dateStr?: string): string {
  if (!dateStr) return '-';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const day = d.getDate();
    const month = MONTH_SHORT[d.getMonth()];
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${day} ${month} ${year} · ${hours}:${minutes}`;
  } catch {
    return dateStr;
  }
}

function formatRupiah(amount: number): string {
  return `Rp. ${Number(amount || 0).toLocaleString('id-ID')}`;
}

function TransactionStatusBadge({ status }: { status: string }) {
  const s = String(status || '').toUpperCase();
  if (s === 'PAID' || s === 'LUNAS') {
    return (
      <span className="inline-flex items-center rounded-full border border-[#0BCF46] bg-[#ECFDF3] px-3 py-1 text-xs font-semibold text-[#027A48]">
        Lunas
      </span>
    );
  }
  if (s === 'DP' || s === 'DP_PAID' || s === 'DP_BALANCE' || s.includes('DP')) {
    return (
      <span className="inline-flex items-center rounded-full border border-[#FC480D] bg-[#FFF5EE] px-3 py-1 text-xs font-semibold text-[#FC480D]">
        DP 50%
      </span>
    );
  }
  if (s === 'PELUNASAN') {
    return (
      <span className="inline-flex items-center rounded-full border border-[#2563EB] bg-[#EFF6FF] px-3 py-1 text-xs font-semibold text-[#2563EB]">
        Pelunasan
      </span>
    );
  }
  if (s === 'PENDING') {
    return (
      <span className="inline-flex items-center rounded-full border border-[#F59E0B] bg-[#FFFBEB] px-3 py-1 text-xs font-semibold text-[#D97706]">
        Pending
      </span>
    );
  }
  if (s === 'CANCELLED' || s === 'BATAL') {
    return (
      <span className="inline-flex items-center rounded-full border border-[#E0E6ED] bg-[#F5F6F7] px-3 py-1 text-xs font-semibold text-gray-500">
        Batal
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full border border-[#E7515A] bg-[#FEF2F2] px-3 py-1 text-xs font-semibold text-[#B91C1C]">
      Unpaid
    </span>
  );
}

function getPaymentMethodDisplay(method?: string, type?: string) {
  const m = String(method || type || '').toLowerCase();
  if (m.includes('cash') || m.includes('tunai')) {
    return { label: 'Cash', icon: 'ph:money-bold', color: 'text-gray-600' };
  }
  if (m.includes('transfer') || m.includes('tf') || m.includes('bank')) {
    return { label: 'Transfer', icon: 'ph:bank-bold', color: 'text-gray-600' };
  }
  if (m.includes('qris')) {
    return { label: 'QRIS', icon: 'ph:qr-code-bold', color: 'text-gray-700' };
  }
  if (m.includes('dana')) {
    return { label: 'Dana', icon: 'ph:wallet-bold', color: 'text-blue-500' };
  }
  if (m.includes('alfamart')) {
    return { label: 'Alfamart', icon: 'ph:storefront-bold', color: 'text-red-500' };
  }
  if (m.includes('indomaret')) {
    return { label: 'Indomaret', icon: 'ph:storefront-bold', color: 'text-blue-600' };
  }
  if (m.includes('shopee')) {
    return { label: 'ShopeePay', icon: 'ph:shopping-bag-bold', color: 'text-orange-500' };
  }
  if (m.includes('bca')) {
    return { label: 'BCA', icon: 'ph:bank-bold', color: 'text-blue-700' };
  }
  if (m.includes('bni')) {
    return { label: 'BNI', icon: 'ph:bank-bold', color: 'text-orange-600' };
  }
  if (m.includes('mandiri')) {
    return { label: 'Mandiri', icon: 'ph:bank-bold', color: 'text-blue-800' };
  }
  if (m.includes('bsi')) {
    return { label: 'BSI', icon: 'ph:bank-bold', color: 'text-teal-600' };
  }
  return { label: method || type || 'Cash', icon: 'ph:credit-card-bold', color: 'text-gray-500' };
}

type DatePreset = 'all' | 'today' | 'yesterday' | '7days' | '30days' | 'thisMonth' | 'lastMonth' | 'custom';

function formatToYMD(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function getDateRangeParams(
  preset: DatePreset,
  customStart?: string,
  customEnd?: string
): { date_from?: string; date_to?: string } {
  const d = new Date();
  if (preset === 'all') {
    return {};
  }
  if (preset === 'today') {
    const todayStr = formatToYMD(d);
    return { date_from: todayStr, date_to: todayStr };
  }
  if (preset === 'yesterday') {
    const y = new Date(d);
    y.setDate(d.getDate() - 1);
    const yStr = formatToYMD(y);
    return { date_from: yStr, date_to: yStr };
  }
  if (preset === '7days') {
    const d7 = new Date(d);
    d7.setDate(d.getDate() - 6);
    return { date_from: formatToYMD(d7), date_to: formatToYMD(d) };
  }
  if (preset === '30days') {
    const d30 = new Date(d);
    d30.setDate(d.getDate() - 29);
    return { date_from: formatToYMD(d30), date_to: formatToYMD(d) };
  }
  if (preset === 'thisMonth') {
    const start = new Date(d.getFullYear(), d.getMonth(), 1);
    const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    return { date_from: formatToYMD(start), date_to: formatToYMD(end) };
  }
  if (preset === 'lastMonth') {
    const start = new Date(d.getFullYear(), d.getMonth() - 1, 1);
    const end = new Date(d.getFullYear(), d.getMonth(), 0);
    return { date_from: formatToYMD(start), date_to: formatToYMD(end) };
  }
  if (preset === 'custom') {
    return {
      date_from: customStart || undefined,
      date_to: customEnd || undefined,
    };
  }
  return {};
}

function getTransactionTypeLabel(inv?: Invoice | null, detail?: InvoiceDetail | null): string {
  const num = String(inv?.invoice_number || detail?.invoice_number || '').toUpperCase();
  if (num.startsWith('POS-')) return 'POS';
  const orderType = String(inv?.order_type || detail?.order_type || '').toLowerCase();
  const category = String(inv?.product_category || '').toLowerCase();
  if (orderType === 'product_purchase' || category === 'pos' || category === 'products') return 'POS';
  if (orderType === 'field_booking' || category === 'lapangan' || category === 'services') return 'Lapangan';
  if (orderType === 'mixed') return 'Campuran';
  if (inv?.product) return inv.product;
  return 'POS';
}

function getTransactionSourceLabel(inv?: Invoice | null, detail?: InvoiceDetail | null): string {
  const num = String(inv?.invoice_number || detail?.invoice_number || '').toUpperCase();
  if (num.startsWith('POS-')) return 'Walk-in';
  const method = String(
    inv?.payment_method || detail?.payment_method || inv?.payment_type || detail?.payment_type || ''
  ).toLowerCase();
  if (method.includes('qris')) return 'App (QRIS)';
  const isPelunasan =
    String(inv?.payment_status || detail?.status || '').toUpperCase() === 'PELUNASAN' ||
    num.endsWith('-PL');
  if (isPelunasan) return 'Walk-in';
  const orderMethod = String(detail?.order_method || inv?.order_type || '').toLowerCase();
  const txType = String(inv?.transaction_type || '').toLowerCase();
  if (orderMethod === 'onsite' || txType === 'manual') return 'Walk-in';
  return 'App';
}

interface CartItem {
  product: Product;
  quantity: number;
}

const TransactionProduct = ({ arenaSlug }: { arenaSlug?: string }) => {
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<string | undefined>();
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | undefined>();

  // Modals & confirmation dialog states
  const [selectedInvoiceNumber, setSelectedInvoiceNumber] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [invoiceToMarkPaid, setInvoiceToMarkPaid] = useState<any>(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [invoiceToCancel, setInvoiceToCancel] = useState<any>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCancelPaidDialogOpen, setIsCancelPaidDialogOpen] = useState(false);
  const [invoiceToCancelPaid, setInvoiceToCancelPaid] = useState<any>(null);
  const [downloadingMap, setDownloadingMap] = useState<Record<string, boolean>>({});

  // Detail Transaksi Side Drawer
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerInvoice, setDrawerInvoice] = useState<Invoice | null>(null);
  const [drawerDetail, setDrawerDetail] = useState<InvoiceDetail | null>(null);
  const [drawerLoading, setDrawerLoading] = useState(false);

  // Receive Payment Modal state
  const [isReceivePaymentOpen, setIsReceivePaymentOpen] = useState(false);
  const [selectedPaymentInvoice, setSelectedPaymentInvoice] = useState<any>(null);

  const [currentArenaSlug, setCurrentArenaSlug] = useState<string>('');

  // Date filter states
  const [datePreset, setDatePreset] = useState<DatePreset>('thisMonth');
  const [isDateMenuOpen, setIsDateMenuOpen] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const dateMenuRef = useRef<HTMLDivElement | null>(null);

  // Filter Drawer states
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filterSource, setFilterSource] = useState<'all' | 'app' | 'walk_in'>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterMethod, setFilterMethod] = useState<string>('all');
  const filterRef = useRef<HTMLDivElement | null>(null);

  // Tambah Transaksi Modal states (POS)
  const [isAddTransactionOpen, setIsAddTransactionOpen] = useState(false);
  const [posCategory, setPosCategory] = useState<'all' | 'sewa' | 'produk'>('all');
  const [posProducts, setPosProducts] = useState<Product[]>([]);
  const [posLoading, setPosLoading] = useState(false);
  const [posCustomerName, setPosCustomerName] = useState('');
  const [posPhoneNumber, setPosPhoneNumber] = useState('');
  const [posPaymentMethod, setPosPaymentMethod] = useState<'Cash' | 'Transfer' | 'QRIS'>('Cash');
  const [posCart, setPosCart] = useState<CartItem[]>([]);
  const [posSubmitting, setPosSubmitting] = useState(false);
  const [qrisFeePercentage, setQrisFeePercentage] = useState<number>(1.5);

  useEffect(() => {
    PaymentMethodApiService.getPaymentMethods()
      .then((methods) => {
        const qris = methods.find(
          (m) => m.code?.toUpperCase() === 'QRIS' || m.name?.toUpperCase() === 'QRIS'
        );
        if (qris && typeof qris.customer_fee_percentage === 'number' && qris.customer_fee_percentage > 0) {
          setQrisFeePercentage(qris.customer_fee_percentage);
        }
      })
      .catch((err) => {
        console.error('Failed to load QRIS fee percentage in TransactionProduct:', err);
      });
  }, []);

  // Initialize arena slug on mount
  useEffect(() => {
    try {
      const slug = arenaSlug || requireArenaSlug();
      setCurrentArenaSlug(slug);
    } catch (err: any) {
      setError(err.message);
      setLoading(false);
    }
  }, [arenaSlug]);

  // Click outside listener for date picker & filter popover
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dateMenuRef.current && !dateMenuRef.current.contains(e.target as Node)) {
        setIsDateMenuOpen(false);
      }
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) {
        setIsFilterOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Fetch products when opening Tambah Transaksi modal
  useEffect(() => {
    if (!isAddTransactionOpen) return;
    let active = true;
    setPosLoading(true);
    void ProductApiService.getProductsByArena(true)
      .then((data) => {
        if (active) setPosProducts(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        console.error('Failed to load products for POS:', err);
        if (active) setPosProducts([]);
      })
      .finally(() => {
        if (active) setPosLoading(false);
      });
    return () => {
      active = false;
    };
  }, [isAddTransactionOpen]);

  // Load invoices data
  const loadInvoices = async () => {
    if (!currentArenaSlug) return;

    try {
      setLoading(true);
      setError(null);

      const apiFilters: ArenaInvoiceApiFilters = {};

      const { date_from, date_to } = getDateRangeParams(datePreset, customStartDate, customEndDate);
      if (date_from) apiFilters.date_from = date_from;
      if (date_to) apiFilters.date_to = date_to;

      if (filterStatus !== 'all') {
        apiFilters.status = filterStatus.toLowerCase();
      }

      if (filterSource === 'walk_in') {
        apiFilters.transaction_type = 'onsite';
      } else if (filterSource === 'app') {
        apiFilters.transaction_type = 'online';
      }

      if (filterMethod !== 'all') {
        apiFilters.payment_method = filterMethod;
      }

      const response = await invoiceService.getArenaInvoices(
        currentArenaSlug,
        page,
        25,
        sortBy,
        sortOrder,
        apiFilters
      );

      if (response.success) {
        setInvoices(response?.data || []);
        setTotalItems(response?.pagination?.total || 0);
      } else {
        throw new Error(response.message || 'Failed to load invoices');
      }
    } catch (err: any) {
      if (err.message && err.message.includes('Authentication required')) {
        setError('Please log in to access transaction data.');
      } else if (err.message && err.message.includes('Session expired')) {
        setError('Your session has expired. Please log in again.');
      } else {
        setError(err.message || 'Failed to load transaction data');
      }
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDrawer = async (inv: Invoice) => {
    setDrawerInvoice(inv);
    setIsDrawerOpen(true);
    setDrawerDetail(null);
    setDrawerLoading(true);

    try {
      const slug = currentArenaSlug || requireArenaSlug();
      const res = await invoiceService.getInvoiceDetail(slug, inv.invoice_number);
      if (res && res.data) {
        setDrawerDetail(res.data);
      }
    } catch (err) {
      console.error('Failed to load invoice detail for drawer:', err);
    } finally {
      setDrawerLoading(false);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isDrawerOpen) {
        setIsDrawerOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isDrawerOpen]);

  useEffect(() => {
    if (currentArenaSlug) {
      void loadInvoices();
    }
  }, [
    page,
    currentArenaSlug,
    sortBy,
    sortOrder,
    datePreset,
    customStartDate,
    customEndDate,
    filterStatus,
    filterSource,
    filterMethod,
  ]);

  const handleSort = (key: string) => {
    if (sortBy === key) {
      if (sortOrder === 'asc') {
        setSortOrder('desc');
      } else if (sortOrder === 'desc') {
        setSortBy(undefined);
        setSortOrder(undefined);
      }
    } else {
      setSortBy(key);
      setSortOrder('asc');
    }
    setPage(1);
  };

  // Date Presets calculation
  const now = new Date();
  const datePresetsMeta = useMemo(() => {
    const todayStr = `${now.getDate()} ${MONTH_SHORT[now.getMonth()]}`;
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    const yesterdayStr = `${yesterday.getDate()} ${MONTH_SHORT[yesterday.getMonth()]}`;

    const d7 = new Date(now);
    d7.setDate(now.getDate() - 6);
    const d7Str = `${d7.getDate()} ${MONTH_SHORT[d7.getMonth()]} - ${todayStr}`;

    const d30 = new Date(now);
    d30.setDate(now.getDate() - 29);
    const d30Str = `${d30.getDate()} ${MONTH_SHORT[d30.getMonth()]} - ${todayStr}`;

    const monthStartStr = `1 ${MONTH_SHORT[now.getMonth()]} - ${todayStr} ${now.getFullYear()}`;

    const prevMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
    const prevMonthStart = new Date(prevMonthEnd.getFullYear(), prevMonthEnd.getMonth(), 1);
    const prevMonthStr = `1 ${MONTH_SHORT[prevMonthStart.getMonth()]} - ${prevMonthEnd.getDate()} ${MONTH_SHORT[prevMonthEnd.getMonth()]}`;

    return [
      { id: 'all' as DatePreset, label: 'Semua Periode', sublabel: 'Tanpa batas' },
      { id: 'today' as DatePreset, label: 'Hari Ini', sublabel: todayStr },
      { id: 'yesterday' as DatePreset, label: 'Kemarin', sublabel: yesterdayStr },
      { id: '7days' as DatePreset, label: '7 hari terakhir', sublabel: d7Str },
      { id: '30days' as DatePreset, label: '30 hari terakhir', sublabel: d30Str },
      { id: 'thisMonth' as DatePreset, label: 'Bulan ini', sublabel: monthStartStr },
      { id: 'lastMonth' as DatePreset, label: 'Bulan lalu', sublabel: prevMonthStr },
    ];
  }, [now]);

  const dateFilterLabel = useMemo(() => {
    if (datePreset === 'all') return 'Semua Tanggal';
    if (datePreset === 'today') return `Hari ini · ${now.getDate()} ${MONTH_SHORT[now.getMonth()]}`;
    if (datePreset === 'yesterday') return 'Kemarin';
    if (datePreset === '7days') return '7 hari terakhir';
    if (datePreset === '30days') return '30 hari terakhir';
    if (datePreset === 'thisMonth') return `Bulan ini · 1 ${MONTH_SHORT[now.getMonth()]} - ${now.getDate()} ${MONTH_SHORT[now.getMonth()]} ${now.getFullYear()}`;
    if (datePreset === 'lastMonth') return 'Bulan lalu';
    if (datePreset === 'custom' && (customStartDate || customEndDate)) {
      return `Custom · ${customStartDate || '...'} s/d ${customEndDate || '...'}`;
    }
    return 'Pilih Tanggal';
  }, [datePreset, customStartDate, customEndDate, now]);

  // Filtering invoices client-side (for instant search bar)
  const filteredInvoices = useMemo(() => {
    return invoices.filter((invoice) => {
      // 1. Search Query
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const matchName = (invoice.customer_name || '').toLowerCase().includes(query);
        const matchNumber = (invoice.invoice_number || '').toLowerCase().includes(query);
        const matchProduct = (invoice.product || '').toLowerCase().includes(query);
        if (!matchName && !matchNumber && !matchProduct) return false;
      }

      return true;
    });
  }, [invoices, searchQuery]);

  const activeFilterCount =
    (filterSource !== 'all' ? 1 : 0) +
    (filterStatus !== 'all' ? 1 : 0) +
    (filterMethod !== 'all' ? 1 : 0);

  const drawerItems = useMemo(() => {
    if (!drawerInvoice && !drawerDetail) return [];

    const items: { name: string; subtitle: string; total: number }[] = [];

    // 1. From product_items
    if (drawerDetail?.product_items && drawerDetail.product_items.length > 0) {
      drawerDetail.product_items.forEach((p) => {
        const qty = p.quantity || 1;
        const unit = p.unit_price || (p.total_price ? p.total_price / qty : 0);
        items.push({
          name: p.product_name,
          subtitle: `${qty} × ${formatRupiah(unit)}`,
          total: p.total_price || qty * unit,
        });
      });
    }

    // 2. From field_bookings
    if (drawerDetail?.field_bookings && drawerDetail.field_bookings.length > 0) {
      const isDP =
        drawerDetail?.payment_terms?.toLowerCase() === 'dp' ||
        drawerDetail?.status?.toLowerCase() === 'partial' ||
        drawerInvoice?.payment_status?.toUpperCase()?.includes('DP') ||
        drawerInvoice?.payment_type === 'dp_balance';

      drawerDetail.field_bookings.forEach((fb) => {
        let datePart = '';
        if (fb.booking_date) {
          try {
            const d = new Date(fb.booking_date);
            datePart = `${d.getDate()} ${MONTH_SHORT[d.getMonth()]}`;
          } catch {
            datePart = fb.booking_date;
          }
        }
        const timePart =
          fb.start_time && fb.end_time
            ? `${fb.start_time.slice(0, 5)} - ${fb.end_time.slice(0, 5)}`
            : '';
        const subtitle = [datePart, timePart].filter(Boolean).join(' · ');

        const itemPrice =
          isDP &&
          drawerDetail.paid_amount > 0 &&
          drawerDetail.total_booking_amount &&
          drawerDetail.total_booking_amount > drawerDetail.paid_amount
            ? fb.final_price * (drawerDetail.paid_amount / drawerDetail.total_booking_amount)
            : fb.final_price || 0;

        items.push({
          name: fb.field_name || 'Booking Lapangan',
          subtitle: isDP ? `${subtitle || 'Sewa Lapangan'} · DP` : subtitle || 'Sewa Lapangan',
          total: itemPrice,
        });
      });
    }

    // Fallback: if no items parsed yet, use invoice.product
    if (items.length === 0 && drawerInvoice) {
      const rawAmt = drawerInvoice.amount || drawerInvoice.subtotal || drawerInvoice.total_amount || 0;
      items.push({
        name: drawerInvoice.product || 'Produk Transaksi',
        subtitle: `1 × ${formatRupiah(rawAmt)}`,
        total: rawAmt,
      });
    }

    return items;
  }, [drawerInvoice, drawerDetail]);

  const drawerRawAmount =
    drawerDetail?.amount ||
    drawerDetail?.paid_amount ||
    drawerDetail?.total_amount ||
    drawerInvoice?.amount ||
    drawerInvoice?.subtotal ||
    drawerInvoice?.total_amount ||
    0;
  const isDrawerAppOrQris = isAppOrQrisTransaction(drawerInvoice, drawerDetail);
  const drawerNet = isDrawerAppOrQris
    ? drawerDetail?.merchant_receives && drawerDetail.merchant_receives > 0
      ? drawerDetail.merchant_receives
      : drawerInvoice?.merchant_receives && drawerInvoice.merchant_receives > 0
      ? drawerInvoice.merchant_receives
      : drawerRawAmount
    : drawerRawAmount;
  const drawerFee = isDrawerAppOrQris ? Math.max(0, drawerRawAmount - drawerNet) : 0;
  const drawerMethodInfo = getPaymentMethodDisplay(
    drawerDetail?.payment_method || drawerInvoice?.payment_method,
    drawerDetail?.payment_type || drawerInvoice?.payment_type
  );

  // Export CSV
  const handleExportCsv = () => {
    if (!filteredInvoices || filteredInvoices.length === 0) {
      showToast.error('Tidak ada data transaksi untuk diexport');
      return;
    }

    const headers = [
      'Tanggal Transaksi',
      'ID Invoice',
      'Pelanggan',
      'Produk',
      'Kategori',
      'Metode',
      'Status',
      'Nominal',
      'Platform Fee',
      'Pendapatan Bersih',
    ];

    const rows = filteredInvoices.map((inv) => {
      const rawAmt = inv.amount || inv.subtotal || inv.total_amount || 0;
      const hasFee = isAppOrQrisTransaction(inv);
      const net = hasFee && inv.merchant_receives > 0 ? inv.merchant_receives : rawAmt;
      const fee = hasFee ? Math.max(0, rawAmt - net) : 0;
      return [
        `"${formatTransactionDate(inv.transaction_date)}"`,
        `"${inv.invoice_number || ''}"`,
        `"${(inv.customer_name || '').replace(/"/g, '""')}"`,
        `"${(inv.product || '').replace(/"/g, '""')}"`,
        `"${inv.product_category || ''}"`,
        `"${inv.payment_method || inv.payment_type || ''}"`,
        `"${inv.payment_status || ''}"`,
        rawAmt,
        fee,
        net,
      ].join(',');
    });

    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transaksi-${currentArenaSlug || 'arena'}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    showToast.success('Export CSV berhasil didownload');
  };

  // Modals & Action Handlers
  const handleViewInvoice = (invoiceNumber: string) => {
    setSelectedInvoiceNumber(invoiceNumber);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedInvoiceNumber('');
  };

  const handleOpenReceivePayment = (inv: any) => {
    setSelectedPaymentInvoice(inv);
    setIsReceivePaymentOpen(true);
  };

  const handlePaymentSuccess = (updatedInvoiceNumber: string, method: string) => {
    setInvoices((prev) =>
      prev.map((inv) =>
        inv.invoice_number === updatedInvoiceNumber
          ? {
              ...inv,
              payment_status: 'PAID',
              payment_method: method,
              payment_type: method,
            }
          : inv
      )
    );
    void loadInvoices();
  };

  const handleDownloadInvoice = async (invoiceNumber: string) => {
    try {
      setDownloadingMap((m) => ({ ...m, [invoiceNumber]: true }));
      const arena = currentArenaSlug || requireArenaSlug();
      const blob = await invoiceService.downloadInvoicePdf(arena, invoiceNumber);

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${invoiceNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download invoice:', err);
      showToast.error('Gagal mendownload invoice. Silakan coba lagi.');
    } finally {
      setDownloadingMap((m) => ({ ...m, [invoiceNumber]: false }));
    }
  };

  const handleMarkAsPaidClick = (invoice: any) => {
    setInvoiceToMarkPaid(invoice);
    setIsConfirmDialogOpen(true);
  };

  const handleConfirmMarkAsPaid = async () => {
    if (!invoiceToMarkPaid) return;
    setIsUpdatingStatus(true);
    try {
      const arena = currentArenaSlug || requireArenaSlug();
      const response = await invoiceService.updateInvoiceStatus(
        arena,
        invoiceToMarkPaid.invoice_number,
        'PAID'
      );
      if (response.success) {
        setInvoices((prev) =>
          prev.map((inv) =>
            inv.invoice_number === invoiceToMarkPaid.invoice_number
              ? { ...inv, payment_status: 'PAID' }
              : inv
          )
        );
        showToast.success('Invoice berhasil ditandai sebagai terbayar');
      } else {
        throw new Error(response.message || 'Failed to update invoice status');
      }
    } catch (error: any) {
      showToast.error(`Gagal menandai invoice: ${error.message}`);
    } finally {
      setIsUpdatingStatus(false);
      setIsConfirmDialogOpen(false);
      setInvoiceToMarkPaid(null);
    }
  };

  const handleMarkAsCancelClick = (invoice: any) => {
    setInvoiceToCancel(invoice);
    setIsCancelDialogOpen(true);
  };

  const handleConfirmCancel = async () => {
    if (!invoiceToCancel) return;
    setIsCancelling(true);
    try {
      const arena = currentArenaSlug || requireArenaSlug();
      const response = await invoiceService.updateInvoiceStatus(
        arena,
        invoiceToCancel.invoice_number,
        'CANCELLED'
      );
      if (response.success) {
        setInvoices((prev) =>
          prev.map((inv) =>
            inv.invoice_number === invoiceToCancel.invoice_number
              ? { ...inv, payment_status: 'CANCELLED' as any }
              : inv
          )
        );
        showToast.success('Invoice berhasil dibatalkan');
      } else {
        throw new Error(response.message || 'Failed to cancel invoice');
      }
    } catch (error: any) {
      showToast.error(`Gagal membatalkan invoice: ${error.message}`);
    } finally {
      setIsCancelling(false);
      setIsCancelDialogOpen(false);
      setInvoiceToCancel(null);
    }
  };

  const handleMarkAsCancelAfterPaid = (invoiceNumber: string) => {
    const invoice = invoices.find((inv) => inv.invoice_number === invoiceNumber);
    setInvoiceToCancelPaid(invoice);
    setIsCancelPaidDialogOpen(true);
  };

  const handleConfirmCancelPaidInvoice = async () => {
    if (!invoiceToCancelPaid) return;
    setIsCancelling(true);
    try {
      const arena = currentArenaSlug || requireArenaSlug();
      const response = await invoiceService.cancelPaidInvoice(
        arena,
        invoiceToCancelPaid.invoice_number
      );
      if (response.success) {
        setInvoices((prev) =>
          prev.map((inv) =>
            inv.invoice_number === invoiceToCancelPaid.invoice_number
              ? { ...inv, payment_status: 'CANCELLED' as any }
              : inv
          )
        );
        showToast.success('Invoice terbayar berhasil dibatalkan');
      } else {
        throw new Error(response.message || 'Failed to cancel invoice');
      }
    } catch (error: any) {
      showToast.error(`Gagal membatalkan invoice: ${error.message}`);
    } finally {
      setIsCancelling(false);
      setIsCancelPaidDialogOpen(false);
      setInvoiceToCancelPaid(null);
    }
  };

  const handleDeleteClick = (invoice: any) => {
    setInvoiceToDelete(invoice);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!invoiceToDelete) return;
    setIsDeleting(true);
    try {
      const arena = currentArenaSlug || requireArenaSlug();
      const response = await invoiceService.deleteInvoice(
        arena,
        invoiceToDelete.invoice_number
      );
      if (response.success) {
        setInvoices((prev) =>
          prev.filter((inv) => inv.invoice_number !== invoiceToDelete.invoice_number)
        );
        setTotalItems((prev) => Math.max(0, prev - 1));
        showToast.success('Invoice berhasil dihapus');
      } else {
        throw new Error(response.message || 'Failed to delete invoice');
      }
    } catch (error: any) {
      showToast.error(`Gagal menghapus invoice: ${error.message}`);
    } finally {
      setIsDeleting(false);
      setIsDeleteDialogOpen(false);
      setInvoiceToDelete(null);
    }
  };

  // Row dropdown action options
  const getDropdownMenu = (row: any): DropdownItem[] => {
    const isUnpaid =
      row.payment_status?.toUpperCase() !== 'PAID' &&
      row.payment_status?.toUpperCase() !== 'LUNAS' &&
      row.payment_status?.toUpperCase() !== 'CANCELLED';

    const items: DropdownItem[] = [
      ...(isUnpaid
        ? [
            {
              label: 'Terima Pembayaran',
              icon: 'ph:wallet-bold',
              onClick: () => handleOpenReceivePayment(row),
              success: true,
            },
          ]
        : []),
      {
        label: 'Lihat Invoice',
        icon: 'ph:receipt',
        onClick: () => handleViewInvoice(row.invoice_number),
      },
      {
        label: 'Download Invoice (PDF)',
        icon: 'ph:download-simple',
        onClick: () => {
          if (downloadingMap[row.invoice_number]) return;
          void handleDownloadInvoice(row.invoice_number);
        },
      },
    ];

    if (row.payment_status === 'PAID') {
      items.push({
        label: 'Batalkan Transaksi',
        icon: 'ph:x-circle',
        onClick: () => handleMarkAsCancelAfterPaid(row.invoice_number),
        destructive: true,
      });
    }

    if (
      String(row.transaction_type).toLowerCase() === 'manual' &&
      String(row.payment_status).toLowerCase() === 'pending'
    ) {
      items.push(
        {
          label: 'Tandai Sebagai Terbayar',
          icon: 'ph:check-circle',
          onClick: () => handleMarkAsPaidClick(row),
          success: true,
        },
        {
          label: 'Batalkan Transaksi',
          icon: 'ph:x-circle',
          onClick: () => handleMarkAsCancelClick(row),
          destructive: true,
        },
        {
          label: 'Hapus Transaksi',
          icon: 'ph:trash',
          onClick: () => handleDeleteClick(row),
          destructive: true,
        }
      );
    }

    return items;
  };

  // POS Cart functions
  const handleAddToCart = (product: Product) => {
    setPosCart((prev) => {
      const idx = prev.findIndex((item) => item.product.id === product.id);
      if (idx !== -1) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + 1 };
        return next;
      }
      return [...prev, { product, quantity: 1 }];
    });
  };

  const handleUpdateQuantity = (productId: number, delta: number) => {
    setPosCart((prev) => {
      return prev
        .map((item) => {
          if (item.product.id === productId) {
            const qty = item.quantity + delta;
            return qty > 0 ? { ...item, quantity: qty } : null;
          }
          return item;
        })
        .filter(Boolean) as CartItem[];
    });
  };

  const handleRemoveFromCart = (productId: number) => {
    setPosCart((prev) => prev.filter((item) => item.product.id !== productId));
  };

  const posTotalItemCount = posCart.reduce((sum, item) => sum + item.quantity, 0);
  const posSubtotal = posCart.reduce((sum, item) => {
    const unitPrice = item.product.price || item.product.rent_price || 0;
    return sum + unitPrice * item.quantity;
  }, 0);

  const posAdminFee = posPaymentMethod === 'QRIS'
    ? Math.round((posSubtotal * qrisFeePercentage) / 100)
    : 0;

  // Biaya admin QRIS ke Sporto ditanggung oleh Mitra, sehingga pembeli tetap membayar subtotal produk (misal Rp 45.000)
  const posTotalPrice = posSubtotal;

  const filteredPosProducts = useMemo(() => {
    return posProducts.filter((product) => {
      if (posCategory === 'sewa') {
        const isRental = product.product_type === 'rental' || product.is_rentable;
        if (!isRental) return false;
      } else if (posCategory === 'produk') {
        const isProd = product.product_type === 'product' || product.is_sellable;
        if (!isProd) return false;
      }
      return true;
    });
  }, [posProducts, posCategory]);

  const handleSavePosTransaction = async () => {
    if (posCart.length === 0) {
      showToast.error('Pilih minimal satu produk/sewa');
      return;
    }
    setPosSubmitting(true);
    try {
      const arena = currentArenaSlug || requireArenaSlug();
      const payload: ProductOrderPayload = {
        customerName: posCustomerName.trim() || 'Pelanggan Walk-In',
        phoneNumber: posPhoneNumber.trim(),
        transactionDate: new Date().toISOString().slice(0, 10),
        paymentMethod: posPaymentMethod,
        products: posCart.map((item) => ({
          id: item.product.id,
          name: item.product.name,
          quantity: item.quantity,
          price: item.product.price || item.product.rent_price || 0,
          total: (item.product.price || item.product.rent_price || 0) * item.quantity,
        })),
        totalAmount: posTotalPrice,
      };

      const res = await productOrderService.createProductOrder(arena, payload);
      if (res.success) {
        showToast.success('Transaksi berhasil disimpan!');
        setIsAddTransactionOpen(false);
        setPosCart([]);
        setPosCustomerName('');
        setPosPhoneNumber('');
        void loadInvoices();

        if (posPaymentMethod === 'QRIS') {
          const invNum = res.data?.payment_info?.invoice_number;
          if (invNum) {
            window.open(`/qris/${invNum}`, '_blank');
          } else if (res.data?.payment_info?.invoice_url) {
            window.open(res.data.payment_info.invoice_url, '_blank');
          }
        } else if (res.data?.payment_info?.invoice_url) {
          window.open(res.data.payment_info.invoice_url, '_blank');
        }
      } else {
        throw new Error(res.message || 'Gagal menyimpan transaksi');
      }
    } catch (err: any) {
      console.error('POS order failed:', err);
      showToast.error(`Gagal menyimpan: ${err.message}`);
    } finally {
      setPosSubmitting(false);
    }
  };

  // Pagination calculation
  const totalPages = Math.max(1, Math.ceil(totalItems / 25));
  const renderPaginationButtons = () => {
    const pages: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (page > 3) pages.push('...');
      const start = Math.max(2, page - 1);
      const end = Math.min(totalPages - 1, page + 1);
      for (let i = start; i <= end; i++) {
        if (!pages.includes(i)) pages.push(i);
      }
      if (page < totalPages - 2) pages.push('...');
      if (!pages.includes(totalPages)) pages.push(totalPages);
    }

    return (
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          className="flex h-8 w-8 items-center justify-center rounded-full border border-[#E0E6ED] bg-white text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Icon icon="ph:caret-left-bold" className="h-3.5 w-3.5" />
        </button>

        {pages.map((p, idx) =>
          p === '...' ? (
            <span key={`dots-${idx}`} className="px-1 text-xs text-gray-400">
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => setPage(Number(p))}
              className={cn(
                'flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold transition-colors',
                p === page
                  ? 'bg-[#FC480D] text-white shadow-2xs'
                  : 'border border-[#E0E6ED] bg-white text-gray-700 hover:bg-gray-50'
              )}
            >
              {p}
            </button>
          )
        )}

        <button
          type="button"
          disabled={page >= totalPages}
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          className="flex h-8 w-8 items-center justify-center rounded-full border border-[#E0E6ED] bg-white text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Icon icon="ph:caret-right-bold" className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  };

  if (loading && invoices.length === 0) {
    return (
      <div className="p-4 sm:p-6 space-y-6 max-w-8xl mx-auto pb-10">
        <div className="rounded-2xl border border-[#E0E6ED] bg-white p-12 text-center shadow-2xs">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-orange-50 text-[#FC480D]">
            <Icon icon="ph:spinner-bold" className="h-6 w-6 animate-spin" />
          </div>
          <p className="mt-3 text-sm font-semibold text-gray-600">Memuat data transaksi...</p>
        </div>
      </div>
    );
  }

  if (error && invoices.length === 0) {
    return (
      <div className="p-4 sm:p-6 space-y-6 max-w-8xl mx-auto pb-10">
        <div className="rounded-2xl border border-red-200 bg-white p-12 text-center shadow-2xs">
          <Icon icon="ph:warning-circle-bold" className="mx-auto h-12 w-12 text-red-500" />
          <h3 className="mt-3 text-lg font-semibold text-[#0E1726]">Gagal Memuat Transaksi</h3>
          <p className="mt-1 text-sm text-gray-500">{error}</p>
          <button
            onClick={() => void loadInvoices()}
            className="mt-4 rounded-full bg-[#FC480D] px-6 py-2 text-sm font-semibold text-white shadow-2xs hover:bg-[#E8490F]"
          >
            Coba Lagi
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-8xl mx-auto pb-10">
      {/* Page Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-xl font-semibold text-[#0E1726]">Transaksi</h1>
        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={handleExportCsv}
            className="inline-flex h-10 items-center gap-2 rounded-full border border-[#E0E6ED] bg-white px-4 text-sm font-semibold text-[#0E1726] shadow-2xs transition-colors hover:bg-gray-50"
          >
            <Icon icon="ph:export-bold" className="h-4 w-4 text-gray-600" />
            <span>Export CSV</span>
          </button>

          <button
            type="button"
            onClick={() => setIsAddTransactionOpen(true)}
            className="inline-flex h-10 items-center gap-2 rounded-full bg-[#FC480D] px-5 text-sm font-semibold text-white shadow-2xs transition-colors hover:bg-[#E8490F]"
          >
            <Icon icon="ph:plus-bold" className="h-4 w-4" />
            <span>Tambah Transaksi</span>
          </button>
        </div>
      </div>

      {/* Main Table Card */}
      <div className="rounded-2xl border border-[#E0E6ED] bg-white p-5 sm:p-6 shadow-2xs">
        {/* Toolbar: Search, Date Filter, and General Filter */}
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between mb-5">
          <div className="flex flex-wrap items-center gap-2.5">
            {/* Search Pill Input */}
            <div className="relative w-full sm:w-72">
              <Icon
                icon="ph:magnifying-glass"
                className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400"
              />
              <input
                type="text"
                className="h-10 w-full rounded-full border border-[#E0E6ED] bg-white pl-10 pr-8 text-sm text-[#0E1726] placeholder:text-gray-400 focus:border-[#FC480D] focus:outline-none transition-colors"
                placeholder="Cari nama, ID, atau produk"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <Icon icon="ph:x-circle-fill" className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Date Range Picker Pill */}
            <div className="relative" ref={dateMenuRef}>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsDateMenuOpen((prev) => !prev);
                }}
                className="inline-flex h-10 items-center gap-2 rounded-full border border-[#E0E6ED] bg-white px-4 text-sm font-medium text-[#0E1726] shadow-2xs transition-colors hover:bg-gray-50"
              >
                <Icon
                  icon="ph:calendar-blank-bold"
                  className="h-4 w-4 text-gray-500"
                />
                <span className="truncate max-w-[220px]">{dateFilterLabel}</span>
                <Icon
                  icon="ph:caret-down-bold"
                  className="h-3.5 w-3.5 text-gray-500"
                />
              </button>

              {/* Date Presets Dropdown */}
              {isDateMenuOpen && (
                <div
                  className="absolute left-0 top-full z-40 mt-2 w-72 sm:w-80 rounded-2xl border border-[#E0E6ED] bg-white p-2 shadow-xl"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="space-y-0.5">
                    {datePresetsMeta.map((preset) => {
                      const isSelected = datePreset === preset.id;
                      return (
                        <button
                          key={preset.id}
                          type="button"
                          onClick={() => {
                            setDatePreset(preset.id);
                            setPage(1);
                            setIsDateMenuOpen(false);
                          }}
                          className={cn(
                            'flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-xs sm:text-sm transition-colors',
                            isSelected
                              ? 'bg-[#FFF5EE] font-semibold text-[#FC480D]'
                              : 'text-[#0E1726] hover:bg-[#F6F6F6]'
                          )}
                        >
                          <span>{preset.label}</span>
                          <span className="text-[11px] text-gray-400">{preset.sublabel}</span>
                        </button>
                      );
                    })}
                  </div>

                  <div className="my-2 border-t border-[#E0E6ED]" />

                  {/* Custom Date Inputs */}
                  <div className="px-2 pt-1 pb-1">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-2">
                      CUSTOM
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <input
                          type="date"
                          value={customStartDate}
                          onChange={(e) => setCustomStartDate(e.target.value)}
                          className="h-8 w-full rounded-lg border border-[#E0E6ED] bg-white px-2 text-[11px] text-[#0E1726] focus:border-[#FC480D] focus:outline-none"
                        />
                      </div>
                      <div>
                        <input
                          type="date"
                          value={customEndDate}
                          onChange={(e) => setCustomEndDate(e.target.value)}
                          className="h-8 w-full rounded-lg border border-[#E0E6ED] bg-white px-2 text-[11px] text-[#0E1726] focus:border-[#FC480D] focus:outline-none"
                        />
                      </div>
                    </div>
                    {(customStartDate || customEndDate) && (
                      <button
                        type="button"
                        onClick={() => {
                          setDatePreset('custom');
                          setPage(1);
                          setIsDateMenuOpen(false);
                        }}
                        className="mt-2 w-full rounded-full bg-[#FC480D] py-1 text-xs font-semibold text-white shadow-2xs hover:bg-[#E8490F]"
                      >
                        Terapkan Tanggal
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Toolbar: Filter Popover Trigger */}
          <div className="relative flex items-center justify-end" ref={filterRef}>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setIsFilterOpen((prev) => !prev);
              }}
              className={cn(
                'inline-flex h-10 items-center gap-2 rounded-full border border-[#E0E6ED] bg-white px-4 text-sm font-semibold text-[#0E1726] shadow-2xs transition-colors hover:bg-gray-50',
                activeFilterCount > 0 && 'border-[#FC480D] bg-[#FFF5EE] text-[#FC480D]'
              )}
            >
              <Icon icon="ph:faders-bold" className="h-4 w-4" />
              <span>Filter</span>
              {activeFilterCount > 0 && (
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#FC480D] text-[10px] font-semibold text-white">
                  {activeFilterCount}
                </span>
              )}
            </button>

            {/* Filter Popover Panel */}
            {isFilterOpen && (
              <div
                className="absolute right-0 top-full z-40 mt-2 w-80 sm:w-96 rounded-2xl border border-[#E0E6ED] bg-white p-5 shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between border-b border-[#E0E6ED] pb-3">
                  <h3 className="text-sm font-semibold text-[#0E1726]">Filter</h3>
                  <button
                    type="button"
                    onClick={() => setIsFilterOpen(false)}
                    className="flex h-7 w-7 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                  >
                    <Icon icon="ph:x-bold" className="h-4 w-4" />
                  </button>
                </div>

                <div className="mt-3.5 space-y-4 max-h-[65vh] overflow-y-auto pr-1">
                  {/* Sumber Section */}
                  <div>
                    <label className="block text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-2">
                      SUMBER
                    </label>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setFilterSource('all');
                          setPage(1);
                        }}
                        className={cn(
                          'rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all',
                          filterSource === 'all'
                            ? 'border border-[#FC480D] bg-[#FFF5EE] text-[#FC480D]'
                            : 'border border-[#E0E6ED] bg-white text-gray-700 hover:bg-gray-50'
                        )}
                      >
                        Semua
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setFilterSource('app');
                          setPage(1);
                        }}
                        className={cn(
                          'inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all',
                          filterSource === 'app'
                            ? 'border border-[#FC480D] bg-[#FFF5EE] text-[#FC480D]'
                            : 'border border-[#E0E6ED] bg-white text-gray-700 hover:bg-gray-50'
                        )}
                      >
                        <Icon icon="ph:device-mobile-bold" className="h-3.5 w-3.5" />
                        <span>App</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setFilterSource('walk_in');
                          setPage(1);
                        }}
                        className={cn(
                          'inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all',
                          filterSource === 'walk_in'
                            ? 'border border-[#FC480D] bg-[#FFF5EE] text-[#FC480D]'
                            : 'border border-[#E0E6ED] bg-white text-gray-700 hover:bg-gray-50'
                        )}
                      >
                        <Icon icon="ph:storefront-bold" className="h-3.5 w-3.5" />
                        <span>Walk-In</span>
                      </button>
                    </div>
                  </div>

                  {/* Status Section */}
                  <div>
                    <label className="block text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-2">
                      STATUS
                    </label>
                    <div className="flex flex-wrap gap-1.5">
                      {[
                        { id: 'all', label: 'Semua' },
                        { id: 'PAID', label: 'Lunas' },
                        { id: 'DP', label: 'DP 50%' },
                        { id: 'PELUNASAN', label: 'Pelunasan' },
                        { id: 'PENDING', label: 'Pending' },
                        { id: 'UNPAID', label: 'Unpaid' },
                      ].map((item) => {
                        const isSelected = filterStatus === item.id;
                        return (
                          <button
                            key={item.id}
                            type="button"
                            onClick={() => {
                              setFilterStatus(item.id);
                              setPage(1);
                            }}
                            className={cn(
                              'rounded-full px-3 py-1 text-xs font-semibold transition-all',
                              isSelected
                                ? 'border border-[#FC480D] bg-[#FFF5EE] text-[#FC480D]'
                                : 'border border-[#E0E6ED] bg-white text-gray-700 hover:bg-gray-50'
                            )}
                          >
                            {item.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Metode Section */}
                  <div>
                    <label className="block text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-2">
                      METODE
                    </label>
                    <div className="space-y-2">
                      <div>
                        <button
                          type="button"
                          onClick={() => {
                            setFilterMethod('all');
                            setPage(1);
                          }}
                          className={cn(
                            'rounded-full px-3.5 py-1 text-xs font-semibold transition-all mb-2',
                            filterMethod === 'all'
                              ? 'border border-[#FC480D] bg-[#FFF5EE] text-[#FC480D]'
                              : 'border border-[#E0E6ED] bg-white text-gray-700 hover:bg-gray-50'
                          )}
                        >
                          Semua metode
                        </button>
                      </div>

                      <div>
                        <p className="text-[10px] font-semibold text-gray-400 mb-1">App</p>
                        <div className="flex flex-wrap gap-1.5">
                          {['BCA', 'BNI', 'Mandiri', 'BSI', 'Indomaret', 'Alfamart', 'QRIS', 'Dana', 'ShopeePay'].map(
                            (m) => {
                              const isSelected = filterMethod.toLowerCase() === m.toLowerCase();
                              return (
                                <button
                                  key={m}
                                  type="button"
                                  onClick={() => {
                                    setFilterMethod(m);
                                    setPage(1);
                                  }}
                                  className={cn(
                                    'rounded-full px-2.5 py-1 text-[11px] font-semibold transition-all',
                                    isSelected
                                      ? 'border border-[#FC480D] bg-[#FFF5EE] text-[#FC480D]'
                                      : 'border border-[#E0E6ED] bg-white text-gray-700 hover:bg-gray-50'
                                  )}
                                >
                                  {m}
                                </button>
                              );
                            }
                          )}
                        </div>
                      </div>

                      <div>
                        <p className="text-[10px] font-semibold text-gray-400 mb-1">Walk-in</p>
                        <div className="flex flex-wrap gap-1.5">
                          {['Cash', 'Transfer'].map((m) => {
                            const isSelected = filterMethod.toLowerCase() === m.toLowerCase();
                            return (
                              <button
                                key={m}
                                type="button"
                                onClick={() => {
                                  setFilterMethod(m);
                                  setPage(1);
                                }}
                                className={cn(
                                  'rounded-full px-2.5 py-1 text-[11px] font-semibold transition-all',
                                  isSelected
                                    ? 'border border-[#FC480D] bg-[#FFF5EE] text-[#FC480D]'
                                    : 'border border-[#E0E6ED] bg-white text-gray-700 hover:bg-gray-50'
                                )}
                              >
                                {m}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-5 flex items-center justify-between border-t border-[#E0E6ED] pt-3">
                  <button
                    type="button"
                    onClick={() => {
                      setFilterSource('all');
                      setFilterStatus('all');
                      setFilterMethod('all');
                      setPage(1);
                    }}
                    className="text-xs font-semibold text-gray-500 hover:text-[#FC480D]"
                  >
                    Reset
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsFilterOpen(false)}
                    className="rounded-full bg-[#FC480D] px-5 py-1.5 text-xs font-semibold text-white shadow-2xs hover:bg-[#E8490F]"
                  >
                    Terapkan
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Invoices Table */}
        {filteredInvoices.length === 0 ? (
          <div className="py-16 text-center">
            <Icon icon="ph:receipt-bold" className="mx-auto h-12 w-12 text-gray-300" />
            <h3 className="mt-3 text-base font-semibold text-[#0E1726]">Tidak Ada Transaksi Ditemukan</h3>
            <p className="mt-1 text-xs text-gray-500">
              {searchQuery || activeFilterCount > 0
                ? 'Tidak ada transaksi yang cocok dengan filter atau kata kunci pencarian.'
                : 'Belum ada data transaksi yang tercatat untuk arena ini.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-[#E0E6ED]">
            <table className="w-full text-left text-xs sm:text-sm">
              <thead className="bg-[#F6F6F6] text-[13px] font-semibold text-[#0E1726]">
                <tr className="border-b border-[#E0E6ED]">
                  <th
                    className="py-3.5 px-4 font-semibold cursor-pointer select-none hover:text-[#FC480D]"
                    onClick={() => handleSort('transaction_date')}
                  >
                    <div className="flex items-center gap-1.5">
                      <span>Tanggal transaksi</span>
                      {sortBy === 'transaction_date' && (
                        <Icon
                          icon={sortOrder === 'asc' ? 'ph:caret-up-bold' : 'ph:caret-down-bold'}
                          className="h-3 w-3 text-[#FC480D]"
                        />
                      )}
                    </div>
                  </th>
                  <th className="py-3.5 px-4 font-semibold">ID</th>
                  <th
                    className="py-3.5 px-4 font-semibold cursor-pointer select-none hover:text-[#FC480D]"
                    onClick={() => handleSort('customer_name')}
                  >
                    <div className="flex items-center gap-1.5">
                      <span>Pelanggan</span>
                      {sortBy === 'customer_name' && (
                        <Icon
                          icon={sortOrder === 'asc' ? 'ph:caret-up-bold' : 'ph:caret-down-bold'}
                          className="h-3 w-3 text-[#FC480D]"
                        />
                      )}
                    </div>
                  </th>
                  <th className="py-3.5 px-4 font-semibold">Produk</th>
                  <th className="py-3.5 px-4 font-semibold">Metode</th>
                  <th className="py-3.5 px-4 font-semibold">Status</th>
                  <th
                    className="py-3.5 px-4 font-semibold text-right cursor-pointer select-none hover:text-[#FC480D]"
                    onClick={() => handleSort('amount')}
                  >
                    <div className="flex items-center justify-end gap-1.5">
                      <span>Nominal</span>
                      {sortBy === 'amount' && (
                        <Icon
                          icon={sortOrder === 'asc' ? 'ph:caret-up-bold' : 'ph:caret-down-bold'}
                          className="h-3 w-3 text-[#FC480D]"
                        />
                      )}
                    </div>
                  </th>
                  <th className="py-3.5 px-4 font-semibold text-right">Platform fee</th>
                  <th className="py-3.5 px-4 font-semibold text-right">Pendapatan bersih</th>
                  <th className="py-3.5 px-4 font-semibold text-center">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E0E6ED] bg-white text-xs sm:text-[13px]">
                {filteredInvoices.map((inv) => {
                  const rawAmt = inv.amount || inv.subtotal || inv.total_amount || 0;
                  const hasFee = isAppOrQrisTransaction(inv);
                  const net = hasFee ? (inv.merchant_receives > 0 ? inv.merchant_receives : rawAmt) : rawAmt;
                  const fee = hasFee ? Math.max(0, rawAmt - net) : 0;
                  const methodInfo = getPaymentMethodDisplay(inv.payment_method, inv.payment_type);

                  return (
                    <tr
                      key={inv.invoice_number || inv.invoice_id}
                      onClick={() => handleOpenDrawer(inv)}
                      className="hover:bg-[#FCFDFD] transition-colors cursor-pointer select-none"
                    >
                      {/* Tanggal */}
                      <td className="py-3.5 px-4 whitespace-nowrap text-gray-600 font-medium">
                        {formatTransactionDate(inv.transaction_date)}
                      </td>

                      {/* ID */}
                      <td className="py-3.5 px-4 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1.5 font-mono text-xs font-semibold text-[#0E1726]">
                          <Icon icon="ph:receipt" className="h-4 w-4 text-gray-400" />
                          <span>{inv.invoice_number}</span>
                        </span>
                      </td>

                      {/* Pelanggan */}
                      <td className="py-3.5 px-4 font-semibold text-[#0E1726]">
                        <span className="truncate max-w-[150px] block" title={inv.customer_name}>
                          {inv.customer_name || '-'}
                        </span>
                      </td>

                      {/* Produk */}
                      <td className="py-3.5 px-4 text-gray-700">
                        <span className="truncate max-w-[280px] lg:max-w-[360px] block" title={inv.product}>
                          {inv.product || '-'}
                        </span>
                      </td>

                      {/* Metode */}
                      <td className="py-3.5 px-4 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1.5 text-xs text-gray-700">
                          <Icon icon={methodInfo.icon} className={cn('h-4 w-4', methodInfo.color)} />
                          <span>{methodInfo.label}</span>
                        </span>
                      </td>

                      {/* Status */}
                      <td className="py-3.5 px-4 whitespace-nowrap">
                        <TransactionStatusBadge status={inv.payment_status} />
                      </td>

                      {/* Nominal */}
                      <td className="py-3.5 px-4 whitespace-nowrap text-right font-semibold text-[#0E1726]">
                        {formatRupiah(rawAmt)}
                      </td>

                      {/* Platform Fee */}
                      <td className="py-3.5 px-4 whitespace-nowrap text-right">
                        {fee > 0 ? (
                          <span className="font-medium text-rose-600">- {formatRupiah(fee)}</span>
                        ) : (
                          <span className="text-gray-400">Rp. 0</span>
                        )}
                      </td>

                      {/* Pendapatan Bersih */}
                      <td className="py-3.5 px-4 whitespace-nowrap text-right font-semibold text-[#027A48]">
                        {formatRupiah(net)}
                      </td>

                      {/* Aksi */}
                      <td
                        className="py-3.5 px-4 whitespace-nowrap text-center"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex items-center justify-end gap-2 w-full">
                          {/* Button Terima Pembayaran: hanya muncul di status yang belum lunas dan masih ada sisa tagihan */}
                          {inv.payment_status?.toUpperCase() !== 'PAID' &&
                            inv.payment_status?.toUpperCase() !== 'LUNAS' &&
                            inv.payment_status?.toUpperCase() !== 'PELUNASAN' &&
                            inv.payment_status?.toUpperCase() !== 'CANCELLED' &&
                            (inv.remaining_amount === undefined || inv.remaining_amount > 0) && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenReceivePayment(inv);
                                }}
                                title="Terima Pembayaran"
                                className="flex h-8 w-8 items-center justify-center rounded-full bg-[#FC480D] text-white shadow-2xs transition-colors hover:bg-[#E8490F]"
                              >
                                <Icon icon="ph:wallet-bold" className="h-4 w-4" />
                              </button>
                            )}

                          {/* Button Show Invoice / Struk */}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewInvoice(inv.invoice_number);
                            }}
                            title="Lihat Struk Invoice"
                            className="flex h-8 w-8 items-center justify-center rounded-full border border-[#E0E6ED] bg-white text-gray-500 shadow-2xs transition-colors hover:bg-gray-50 hover:text-[#0E1726]"
                          >
                            <Icon icon="ph:receipt" className="h-4 w-4" />
                          </button>

                          <DropdownButton
                            items={getDropdownMenu(inv)}
                            triggerVariant="ghost"
                            triggerSize="sm"
                            triggerClassName="flex h-8 w-8 items-center justify-center rounded-full border border-[#E0E6ED] bg-white p-0 text-gray-500 shadow-2xs transition-colors hover:border-[#FC480D] hover:bg-[#FFF5EE] hover:text-[#FC480D]"
                            icon="ph:arrow-up-right-bold"
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Table Footer: Summary & Pagination */}
        <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between pt-2">
          <p className="text-xs text-gray-500">
            Menampilkan{' '}
            <span className="font-semibold text-gray-700">
              {filteredInvoices.length === 0 ? 0 : (page - 1) * 25 + 1}
            </span>{' '}
            -{' '}
            <span className="font-semibold text-gray-700">
              {Math.min(page * 25, totalItems)}
            </span>{' '}
            dari <span className="font-semibold text-gray-700">{totalItems.toLocaleString('id-ID')}</span>
          </p>
          <div>{renderPaginationButtons()}</div>
        </div>
      </div>

      {/* Tambah Transaksi Modal (POS) */}
      <Dialog
        open={isAddTransactionOpen}
        onOpenChange={(open) => {
          if (!open) setIsAddTransactionOpen(false);
        }}
      >
        <DialogContent
          showCloseButton={false}
          className="max-w-4xl rounded-2xl border border-[#E0E6ED] bg-white p-6 shadow-2xl sm:max-w-4xl"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-[#E0E6ED] pb-3">
            <h2 className="text-lg font-semibold text-[#0E1726]">Tambah Transaksi</h2>
            <button
              type="button"
              onClick={() => setIsAddTransactionOpen(false)}
              className="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
            >
              <Icon icon="ph:x-bold" className="h-4 w-4" />
            </button>
          </div>

          {/* Modal Content: 2 Columns */}
          <div className="mt-4 grid grid-cols-1 gap-6 lg:grid-cols-12 max-h-[75vh] overflow-y-auto pr-1">
            {/* Left Column: Products List & Category Filter (col-span-7) */}
            <div className="lg:col-span-7 space-y-4">
              {/* Category Filter Pills */}
              <div className="flex items-center gap-2">
                {[
                  { id: 'all', label: 'Semua' },
                  { id: 'sewa', label: 'Sewa' },
                  { id: 'produk', label: 'Produk' },
                ].map((tab) => {
                  const isSelected = posCategory === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setPosCategory(tab.id as any)}
                      className={cn(
                        'rounded-full px-4 py-1.5 text-xs font-semibold transition-all',
                        isSelected
                          ? 'border border-[#FC480D] bg-[#FFF5EE] text-[#FC480D]'
                          : 'border border-[#E0E6ED] bg-white text-gray-700 hover:bg-gray-50'
                      )}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>

              {/* Products Grid */}
              {posLoading ? (
                <div className="py-12 text-center text-xs text-gray-400">
                  <Icon icon="ph:spinner-bold" className="mx-auto h-6 w-6 animate-spin text-[#FC480D]" />
                  <p className="mt-2">Memuat daftar produk...</p>
                </div>
              ) : filteredPosProducts.length === 0 ? (
                <div className="rounded-xl border border-dashed border-[#E0E6ED] p-8 text-center text-xs text-gray-400">
                  <Icon icon="ph:package" className="mx-auto h-8 w-8 text-gray-300 mb-1" />
                  Belum ada produk atau sewa tersedia
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {filteredPosProducts.map((prod) => {
                    const price = prod.price || prod.rent_price || 0;
                    return (
                      <div
                        key={prod.id}
                        className="flex flex-col justify-between rounded-2xl border border-[#E0E6ED] bg-white p-3 shadow-2xs hover:border-gray-300 transition-colors"
                      >
                        <div>
                          {/* Image Thumbnail / Placeholder */}
                          <div className="flex h-24 w-full flex-col items-center justify-center rounded-xl bg-[#F5F6F7] text-gray-400 mb-2 overflow-hidden">
                            {prod.images && prod.images.length > 0 ? (
                              <img
                                src={prod.images[0]}
                                alt={prod.name}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <div className="flex flex-col items-center justify-center">
                                <Icon icon="ph:image" className="h-6 w-6" />
                                <span className="mt-1 text-[10px]">Foto produk</span>
                              </div>
                            )}
                          </div>

                          <h4 className="text-xs font-semibold text-[#0E1726] truncate" title={prod.name}>
                            {prod.name}
                          </h4>
                          <p className="mt-0.5 text-xs text-gray-500 font-medium">
                            {formatRupiah(price)}
                          </p>
                        </div>

                        <button
                          type="button"
                          onClick={() => handleAddToCart(prod)}
                          className="mt-3 w-full rounded-full border border-orange-200 bg-[#FFF5EE] py-1.5 text-xs font-semibold text-[#FC480D] transition-colors hover:bg-orange-100"
                        >
                          + Tambah
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Right Column: Order Summary & Checkout (col-span-5) */}
            <div className="lg:col-span-5 flex flex-col justify-between border-t border-[#E0E6ED] pt-4 lg:border-t-0 lg:border-l lg:pl-6 lg:pt-0">
              <div className="space-y-4">
                {/* Customer Name Input */}
                <div>
                  <label className="block text-xs font-semibold text-[#0E1726] mb-1.5">
                    Nama pelanggan (opsional)
                  </label>
                  <input
                    type="text"
                    placeholder="Contoh: Garuda Muda FC"
                    value={posCustomerName}
                    onChange={(e) => setPosCustomerName(e.target.value)}
                    className="h-10 w-full rounded-full border border-[#E0E6ED] bg-white px-4 text-xs sm:text-sm text-[#0E1726] placeholder:text-gray-400 focus:border-[#FC480D] focus:outline-none"
                  />
                </div>

                {/* Selected Items List */}
                <div>
                  <label className="block text-xs font-semibold text-[#0E1726] mb-1.5">
                    Item Terpilih ({posTotalItemCount})
                  </label>
                  {posCart.length === 0 ? (
                    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-[#E0E6ED] py-8 text-center text-xs text-gray-400">
                      <Icon icon="ph:package" className="h-8 w-8 text-gray-300 mb-1" />
                      <p>Belum ada item. Pilih produk di sebelah kiri.</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                      {posCart.map((item) => {
                        const unitPrice = item.product.price || item.product.rent_price || 0;
                        const sub = unitPrice * item.quantity;
                        return (
                          <div
                            key={item.product.id}
                            className="flex items-center justify-between rounded-xl bg-[#F5F6F7] p-2.5 text-xs"
                          >
                            <div className="min-w-0 flex-1 pr-2">
                              <p className="font-semibold text-[#0E1726] truncate">{item.product.name}</p>
                              <p className="text-gray-500 text-[11px]">{formatRupiah(sub)}</p>
                            </div>

                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => handleUpdateQuantity(item.product.id, -1)}
                                className="flex h-6 w-6 items-center justify-center rounded-full border border-[#E0E6ED] bg-white text-gray-600 hover:bg-gray-100"
                              >
                                -
                              </button>
                              <span className="w-5 text-center font-semibold text-[#0E1726]">
                                {item.quantity}
                              </span>
                              <button
                                type="button"
                                onClick={() => handleUpdateQuantity(item.product.id, 1)}
                                className="flex h-6 w-6 items-center justify-center rounded-full border border-[#E0E6ED] bg-white text-gray-600 hover:bg-gray-100"
                              >
                                +
                              </button>

                              <button
                                type="button"
                                onClick={() => handleRemoveFromCart(item.product.id)}
                                className="ml-1 text-gray-400 hover:text-red-500"
                              >
                                <Icon icon="ph:trash" className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Metode Pembayaran */}
                <div>
                  <label className="block text-xs font-semibold text-[#0E1726] mb-1.5">
                    Metode pembayaran
                  </label>
                  <div className="flex items-center gap-2">
                    {[
                      { id: 'Cash', icon: 'ph:money-bold' },
                      { id: 'Transfer', icon: 'ph:bank-bold' },
                      { id: 'QRIS', icon: 'ph:qr-code-bold', badge: `${qrisFeePercentage}%` },
                    ].map((m) => {
                      const isSelected = posPaymentMethod === m.id;
                      return (
                        <button
                          key={m.id}
                          type="button"
                          onClick={() => setPosPaymentMethod(m.id as any)}
                          className={cn(
                            'inline-flex flex-1 items-center justify-center gap-1.5 rounded-full border py-2 text-xs font-semibold transition-all',
                            isSelected
                              ? 'border-[#FC480D] bg-[#FFF5EE] text-[#FC480D]'
                              : 'border-[#E0E6ED] bg-white text-gray-700 hover:bg-gray-50'
                          )}
                        >
                          <Icon icon={m.icon} className="h-3.5 w-3.5" />
                          <span>{m.id}</span>
                          {m.badge && (
                            <span
                              className={cn(
                                'text-[10px] px-1 py-0.5 rounded font-medium',
                                isSelected ? 'bg-[#FC480D]/10 text-[#FC480D]' : 'bg-gray-100 text-gray-500'
                              )}
                            >
                              {m.badge}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Total & Submit Button */}
              <div className="mt-6 border-t border-[#E0E6ED] pt-4 space-y-3">
                {posPaymentMethod === 'QRIS' && posAdminFee > 0 && (
                  <div className="space-y-1.5 pb-2.5 border-b border-dashed border-[#E0E6ED] text-xs">
                    <div className="flex items-center justify-between text-gray-500">
                      <span>Subtotal ({posTotalItemCount} Item)</span>
                      <span className="font-medium text-[#0E1726]">{formatRupiah(posSubtotal)}</span>
                    </div>
                  </div>
                )}

                <div className="flex items-baseline justify-between">
                  <span className="text-xs text-gray-500">
                    {posPaymentMethod === 'QRIS' ? 'Total Bayar (Pembeli)' : `Total · ${posTotalItemCount} Item`}
                  </span>
                  <span className="text-lg sm:text-xl font-semibold text-[#FC480D]">
                    {formatRupiah(posTotalPrice)}
                  </span>
                </div>

                <button
                  type="button"
                  disabled={posSubmitting || posCart.length === 0}
                  onClick={() => void handleSavePosTransaction()}
                  className="h-11 w-full rounded-full bg-[#FC480D] text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#E8490F] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {posSubmitting ? 'Menyimpan...' : 'Simpan Transaksi'}
                </button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Struk / Invoice Modal */}
      <TransactionReceiptModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        invoiceNumber={selectedInvoiceNumber}
        arenaSlug={currentArenaSlug || requireArenaSlug()}
        fallbackInvoice={invoices.find((i) => i.invoice_number === selectedInvoiceNumber)}
      />

      {/* Receive Payment Modal */}
      <ReceivePaymentModal
        isOpen={isReceivePaymentOpen}
        onClose={() => {
          setIsReceivePaymentOpen(false);
          setSelectedPaymentInvoice(null);
        }}
        invoice={selectedPaymentInvoice}
        arenaSlug={currentArenaSlug || requireArenaSlug()}
        onSuccess={handlePaymentSuccess}
      />

      {/* Confirm: Mark as Paid */}
      <ConfirmDialog
        isOpen={isConfirmDialogOpen}
        title="Konfirmasi Pembayaran"
        message={`Apakah Anda yakin ingin menandai invoice "${invoiceToMarkPaid?.invoice_number}" sebagai TERBAYAR? Tindakan ini tidak dapat dibatalkan.`}
        onConfirm={handleConfirmMarkAsPaid}
        onCancel={() => {
          if (!isUpdatingStatus) setIsConfirmDialogOpen(false);
        }}
        loading={isUpdatingStatus}
        confirmText="Ya, Tandai Terbayar"
        cancelText="Batal"
      />

      {/* Confirm: Cancel Transaction */}
      <ConfirmDialog
        isOpen={isCancelDialogOpen}
        title="Konfirmasi Batalkan Transaksi"
        message={`Apakah Anda yakin ingin membatalkan transaksi "${invoiceToCancel?.invoice_number}"? Tindakan ini tidak dapat dibatalkan.`}
        onConfirm={handleConfirmCancel}
        onCancel={() => {
          if (!isCancelling) setIsCancelDialogOpen(false);
        }}
        loading={isCancelling}
        confirmText="Ya, Batalkan Transaksi"
        cancelText="Tidak"
      />

      {/* Confirm: Delete Transaction */}
      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        title="Konfirmasi Hapus Transaksi"
        message={`Apakah Anda yakin ingin MENGHAPUS PERMANEN transaksi "${invoiceToDelete?.invoice_number}"? Data ini tidak dapat dikembalikan setelah dihapus.`}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          if (!isDeleting) setIsDeleteDialogOpen(false);
        }}
        loading={isDeleting}
        confirmText="Ya, Hapus Permanen"
        cancelText="Batal"
      />

      {/* Confirm: Cancel Paid Invoice */}
      <ConfirmDialog
        isOpen={isCancelPaidDialogOpen}
        title="Konfirmasi Batalkan Invoice Terbayar"
        message={`Apakah Anda yakin ingin membatalkan invoice terbayar "${invoiceToCancelPaid?.invoice_number}"? Tindakan ini akan mengubah status invoice menjadi CANCELLED.`}
        onConfirm={handleConfirmCancelPaidInvoice}
        onCancel={() => {
          if (!isCancelling) setIsCancelPaidDialogOpen(false);
        }}
        loading={isCancelling}
        confirmText="Ya, Batalkan Invoice"
        cancelText="Tidak"
      />
      {/* Transaction Detail Slide-Over Drawer */}
      <div
        className={cn(
          'fixed inset-0 z-50 bg-black/40 backdrop-blur-xs transition-opacity duration-300 !mt-0',
          isDrawerOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        )}
        onClick={() => setIsDrawerOpen(false)}
      />

      <aside
        className={cn(
          'fixed inset-y-0 right-0 z-50 flex w-full max-w-[420px] flex-col bg-white shadow-2xl transition-transform duration-300 ease-in-out border-l border-[#E0E6ED] !mt-0',
          isDrawerOpen ? 'translate-x-0' : 'translate-x-full'
        )}
        aria-label="Detail Transaksi"
      >
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-[#E0E6ED]">
          <div>
            <h2 className="text-base sm:text-lg font-semibold text-[#0E1726]">Detail Transaksi</h2>
            <div className="mt-1 flex items-center gap-2">
              <span className="font-mono text-xs font-semibold text-gray-500">
                {drawerInvoice?.invoice_number}
              </span>
              {drawerInvoice && <TransactionStatusBadge status={drawerInvoice.payment_status} />}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setIsDrawerOpen(false)}
            className="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
            title="Tutup"
          >
            <Icon icon="ph:x-bold" className="h-4 w-4" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Section: TRANSAKSI */}
          <div>
            <h3 className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-3">
              TRANSAKSI
            </h3>
            <div className="space-y-3 text-xs sm:text-[13px]">
              <div className="flex items-center justify-between">
                <span className="text-gray-500">Tanggal</span>
                <span className="font-medium text-[#0E1726]">
                  {formatTransactionDate(drawerInvoice?.transaction_date || drawerDetail?.created_at)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-500">Jenis</span>
                <span className="font-medium text-[#0E1726]">
                  {getTransactionTypeLabel(drawerInvoice, drawerDetail)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-500">Pelanggan</span>
                <span className="font-semibold text-[#0E1726]">
                  {drawerInvoice?.customer_name || drawerDetail?.customer_name || '-'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-500">Sumber</span>
                <span className="font-medium text-[#0E1726]">
                  {getTransactionSourceLabel(drawerInvoice, drawerDetail)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-500">Metode</span>
                <span className="inline-flex items-center gap-1.5 font-medium text-[#0E1726]">
                  <Icon icon={drawerMethodInfo.icon} className={cn('h-4 w-4', drawerMethodInfo.color)} />
                  <span>{drawerMethodInfo.label}</span>
                </span>
              </div>
            </div>
          </div>

          <div className="border-t border-[#E0E6ED]" />

          {/* Section: PRODUK */}
          <div>
            <h3 className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-3">
              PRODUK
            </h3>

            {drawerLoading ? (
              <div className="py-6 text-center text-xs text-gray-400">
                <Icon icon="ph:spinner-bold" className="mx-auto h-5 w-5 animate-spin text-[#FC480D]" />
                <p className="mt-2">Memuat rincian produk...</p>
              </div>
            ) : (
              <div className="space-y-3.5">
                {drawerItems.map((item, idx) => (
                  <div key={idx} className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <h4 className="text-xs sm:text-sm font-semibold text-[#0E1726] truncate">
                        {item.name}
                      </h4>
                      <p className="text-xs text-gray-500 mt-0.5">{item.subtitle}</p>
                    </div>
                    <span className="font-semibold text-xs sm:text-sm text-[#0E1726] whitespace-nowrap">
                      {formatRupiah(item.total)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Section: Summary Card */}
          <div className="rounded-2xl border border-[#FFEADA] bg-[#FFF8F5] p-4 space-y-2.5">
            <div className="flex items-center justify-between text-xs sm:text-[13px]">
              <span className="text-gray-600">Nominal</span>
              <span className="font-medium text-[#0E1726]">{formatRupiah(drawerRawAmount)}</span>
            </div>
            <div className="flex items-center justify-between text-xs sm:text-[13px]">
              <span className="text-gray-600">Platform fee</span>
              <span className="font-medium text-gray-500">
                {drawerFee > 0 ? `- ${formatRupiah(drawerFee)}` : 'Rp. 0'}
              </span>
            </div>
            <div className="flex items-center justify-between text-xs sm:text-[13px] pt-2 border-t border-[#FFE5D2]">
              <span className="font-semibold text-[#0E1726]">Pendapatan bersih</span>
              <span className="font-semibold text-[#027A48]">{formatRupiah(drawerNet)}</span>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
};

export default TransactionProduct;
